import { useState, useEffect } from 'react'
import Card, { CardHeader, CardContent } from '../ui/Card'
import Button from '../ui/Button'
import Input from '../ui/Input'
import LoadingState from '../ui/LoadingState'
import EmptyState from '../ui/EmptyState'
import { reporteService } from '../../services/reporteService'
import Chart from 'react-apexcharts'
import {
  createArtemisAxisOptions,
  createArtemisBaseOptions,
  getArtemisChartTokens,
  getArtemisPalette,
} from '../../utils/artemisApexTheme'
import { FileText, Download, RefreshCw, AlertCircle, Activity, Pill } from 'lucide-react'

export default function ReporteFichas({ idClinica = null }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [fechaInicio, setFechaInicio] = useState(
    new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0]
  )
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0])

  useEffect(() => {
    // Solo hacer fetch si idClinica está definido (incluyendo 0) o si es null explícitamente después de que el usuario se haya cargado
    if (idClinica !== undefined) {
      fetchData()
    }
  }, [fechaInicio, fechaFin, idClinica])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin
      }
      // IMPORTANTE: Solo agregar id_clinica si es un número válido mayor que 0 (no null, no undefined, no 0)
      // null y undefined significan "dejar que el backend lo resuelva automáticamente"
      if (idClinica != null && idClinica !== undefined && !isNaN(Number(idClinica)) && Number(idClinica) > 0) {
        params.id_clinica = Number(idClinica)
      }
      
      const response = await reporteService.getReporteFichas(params)
      if (response.success) {
        setData(response.data)
      } else {
        setError(response.error || 'Error al cargar reporte')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <LoadingState message="Cargando reporte de fichas..." size="lg" />
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <p className="text-red-700 dark:text-red-400">{error}</p>
            <Button onClick={fetchData} variant="secondary" className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return <EmptyState icon={FileText} title="No hay datos disponibles" />
  }

  const palette = getArtemisPalette()
  const tokens = getArtemisChartTokens()
  const axisOptions = createArtemisAxisOptions()

  // Configuración gráfico por estado
  const donutBaseOptions = createArtemisBaseOptions({
    type: 'donut',
    colors: palette.slice(0, 5),
  })
  const chartEstadoOptions = {
    ...donutBaseOptions,
    labels: data.fichasPorEstado.map(item => item.estado),
    stroke: {
      width: 2,
      colors: [tokens.isDark ? '#1a2535' : '#ffffff'],
    },
    plotOptions: {
      pie: {
        donut: {
          size: '66%',
        },
      },
    },
    legend: {
      ...donutBaseOptions.legend,
      position: 'bottom',
    },
  }

  const chartEstadoSeries = data.fichasPorEstado.map(item => item.cantidad)

  // Configuración gráfico mensual
  const lineBaseOptions = createArtemisBaseOptions({
    type: 'line',
    height: 350,
    colors: [palette[4]],
  })
  const chartMensualOptions = {
    ...lineBaseOptions,
    xaxis: {
      ...axisOptions,
      categories: data.fichasMensuales.map(item => item.mesCorto),
    },
    yaxis: {
      ...axisOptions,
      tickAmount: 5,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    markers: {
      size: 4,
      hover: { size: 6 },
    },
    grid: {
      ...lineBaseOptions.grid,
      padding: { top: 8, right: 10, left: 0, bottom: 0 },
    },
  }

  const chartMensualSeries = [{
    name: 'Fichas Clínicas',
    data: data.fichasMensuales.map(item => item.cantidad)
  }]

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-pastel-ink dark:text-white">Filtros de Fecha</h3>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Fecha Inicio"
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
            <Input
              label="Fecha Fin"
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
            <div className="flex items-end">
              <Button onClick={fetchData} variant="secondary" className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pastel-ink/70 dark:text-gray-400">Total Fichas</p>
                <p className="text-3xl font-bold text-pastel-ink dark:text-white mt-2">{data.totalFichas}</p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <FileText className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pastel-ink/70 dark:text-gray-400">En Período</p>
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400 mt-2">{data.fichasEnPeriodo}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pastel-ink/70 dark:text-gray-400">Tratamientos</p>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">{data.totalTratamientos}</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Pill className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pastel-ink/70 dark:text-gray-400">Tratamientos Período</p>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">{data.tratamientosEnPeriodo}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pastel-ink/70 dark:text-gray-400">Seguimientos</p>
                <p className="text-3xl font-bold text-green-700 dark:text-green-400 mt-2">{data.totalSeguimientos}</p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Activity className="w-8 h-8 text-green-700 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-pastel-ink dark:text-white">Fichas por Estado</h3>
          </CardHeader>
          <CardContent>
            <Chart
              options={chartEstadoOptions}
              series={chartEstadoSeries}
              type="donut"
              height={350}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-pastel-ink dark:text-white">Tendencia Mensual</h3>
          </CardHeader>
          <CardContent>
            <Chart
              options={chartMensualOptions}
              series={chartMensualSeries}
              type="line"
              height={350}
            />
          </CardContent>
        </Card>
      </div>

      {/* Top Veterinarios */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-pastel-ink dark:text-white">Top Veterinarios por Fichas</h3>
            <Button variant="secondary" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-pastel-peach/30 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pastel-ink/70 dark:text-gray-400 uppercase tracking-wider">Veterinario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pastel-ink/70 dark:text-gray-400 uppercase tracking-wider">Total Fichas</th>
                </tr>
              </thead>
              <tbody className="bg-pastel-cream dark:bg-gray-800 divide-y divide-pastel-pink/30 dark:divide-gray-700">
                {data.topVeterinarios.map((item, index) => (
                  <tr key={index} className="hover:bg-pastel-peach/30 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-pastel-ink dark:text-white">{item.veterinario}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-pastel-ink/70 dark:text-gray-400">{item.cantidad}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

